import  { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  TextField,
  Alert,
  Tooltip,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Divider
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../theme";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import authService from "../../services/AuthService";
import bidsService from "../../services/BidsService";
import CompactAuthorityToggle from "../../components/authority/CompactAuthorityToggle";
import MarketHeader from "../../components/market/MarketHeader";
import BidsInfoDialog from "../../components/market/BidsInfoDialog";
import { CARD_FOILS } from "../../constants/filterConstants";

/**
 * Get the display name for a foil type
 * @param {number} foilType - The foil type (0-4)
 * @returns {string} The display name for the foil type
 */
const getFoilTypeName = (foilType) => {
  const foil = CARD_FOILS.find(f => f.id === foilType);
  return foil ? foil.name : 'Regular';
};

/**
 * BidsTab Component - Displays and manages user bids for a card
 * 
 * @param {Object} props - Component props
 * @param {Object} props.card - The current card object
 * @param {Object} props.details - The card details object
 * @param {boolean} [props.showHeader=false] - Whether to show the market header
 * @returns {JSX.Element} The rendered component
 */
const BidsTab = ({ card, details, showHeader = false }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  // State for bids management
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Authority management state
  const [hasAuthority, setHasAuthority] = useState(false);
  const [, setAuthorityStatus] = useState(null);
  
  // Info dialog state
  const [showInfo, setShowInfo] = useState(false);
  
  // Bid creation confirmation dialog state
  const [showBidConfirmDialog, setShowBidConfirmDialog] = useState(false);
  const [pendingBidData, setPendingBidData] = useState(null);
  const [dontShowBidInfoAgain, setDontShowBidInfoAgain] = useState(false);
  
  // Inline editing states
  const [editingBidId, setEditingBidId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newBidValues, setNewBidValues] = useState({
    maxPriceBcx: "",     // Price per BCX that user wants to pay
    totalBcx: "10",      // Total BCX amount they want to buy
    maxBcxCard: "1",     // Max BCX per single card
    active: true
  });
  
  // **PERFORMANCE OPTIMIZED**: Function to load bids from SplTrader API with caching
  const loadBids = useCallback(async () => {
    setLoading(true);
    setError(null); // Clear previous errors
    
    try {
      // Get the expected card ID for this card
      const expectedCardId = bidsService.formatCardId(card, details);
      
      // **PERFORMANCE**: Use optimized getBidsWithFetch with caching
      const splTraderBids = await bidsService.getBidsWithFetch();
      

      // Ensure we have an array
      const bidsArray = Array.isArray(splTraderBids) ? splTraderBids : [];
      
      // Filter bids for this specific card BEFORE conversion
      const cardBids = bidsArray
        .filter(bid => {
          // Ensure both values are strings for comparison
          const bidIdStr = String(bid.id);
          const expectedIdStr = String(expectedCardId);
          return bidIdStr === expectedIdStr;
        }) // Filter by SplTrader ID first
        .map(bid => bidsService.convertFromSplTraderFormat(bid, details)); // Then convert
      
      setBids(cardBids);
      setLoading(false);
    } catch (error) {
      console.error("Error loading bids:", error);
      
      // Better error handling - don't show Keychain errors to authenticated users
      let errorMessage = "Error loading bids";
      
      if (error.message.includes('Invalid token')) {
        errorMessage = "Authentication error - please log in again";
      } else if (error.message.includes('Keychain') && authService.isLoggedIn()) {
        errorMessage = "Temporary connection error - please try again";
      } else if (error.message.includes('6 Sekunden') || error.message.includes('abgebrochen')) {
        errorMessage = "Server is responding slowly - please try again in a few seconds";
      } else if (error.message.includes('CORS') || error.message.includes('Netzwerk')) {
        errorMessage = "Connection problem - check your internet connection";
      } else {
        errorMessage = "Error loading bids: " + error.message;
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  }, [card, details]);

  // Get current user on component mount
  useEffect(() => {
    try {
      const user = authService.getCurrentUser();
      
      // Only proceed if we have a real authenticated user
      if (user && user.username) {
        setCurrentUser(user);
        loadBids();
      } else {
        setError("Please log in to view and manage bids");
        setLoading(false);
      }
    } catch (error) {
      console.error("Error getting current user:", error);
      setError("Authentication error - please log in");
      setLoading(false);
    }
  }, [loadBids]); // Include loadBids as dependency
  
  // Handle authority status changes
  const handleAuthorityChange = (status) => {
    setAuthorityStatus(status);
    setHasAuthority(status?.hasAuthority || false);
  };
  
  // Start adding a new bid
  const handleAddBid = () => {
    // Check authority before allowing bid creation
    if (!hasAuthority) {
      setError("You must grant authority before creating bids. Please enable authority above.");
      return;
    }
    
    setError(null); // Clear any previous errors
    
    // Get foil type and auto-fill BCX for special cards
    const parsedCard = bidsService.parseCardId(bidsService.formatCardId(card, details));
    const foilType = parsedCard?.foil || 'regular';
    const maxBcx = getMaxLevelBcx(details, foilType);
    
    // Auto-fill BCX for Black Foil and Gold Arcana cards
    const defaultTotalBcx = maxBcx && isAutoMaxLevel(foilType) ? maxBcx.toString() : "1";
    const defaultMaxBcxCard = maxBcx && isAutoMaxLevel(foilType) ? maxBcx.toString() : "1";
    
    setIsAddingNew(true);
    setNewBidValues({
      maxPriceBcx: "",
      totalBcx: defaultTotalBcx,
      maxBcxCard: defaultMaxBcxCard,
      active: true
    });
  };

  // Cancel adding new bid
  const cancelNewBid = () => {
    setIsAddingNew(false);
    setNewBidValues({
      maxPriceBcx: "",     // Price per BCX that user wants to pay
      totalBcx: "10", 
      maxBcxCard: "1",
      active: true
    });
  };
  
  // Confirm new bid
  const confirmNewBid = async () => {
    // Get values and validate
    const maxPriceBcx = parseFloat(newBidValues.maxPriceBcx);
    const totalBcx = parseInt(newBidValues.totalBcx);
    const maxBcxCard = parseInt(newBidValues.maxBcxCard);
    
    // Validate inputs - but don't close the form, just show error
    if (!newBidValues.maxPriceBcx || isNaN(maxPriceBcx) || maxPriceBcx <= 0) {
      setError("Please enter a valid price per BCX (must be greater than 0)");
      return; // Stay in add mode
    }
    
    if (!newBidValues.totalBcx || isNaN(totalBcx) || totalBcx <= 0) {
      setError("Please enter a valid total BCX (must be greater than 0)");
      return; // Stay in add mode
    }
    
    if (!newBidValues.maxBcxCard || isNaN(maxBcxCard) || maxBcxCard <= 0) {
      setError("Please enter a valid max BCX per card (must be greater than 0)");
      return; // Stay in add mode
    }
    
    if (maxBcxCard > totalBcx) {
      setError("Max BCX per card cannot be greater than total BCX. Please adjust your values.");
      return; // Stay in add mode so user can fix the values
    }

    // Clear any validation errors since we passed all checks
    setError(null);

    // Check if bid price is above market price (warning but not blocking)
    const marketPriceBcx = card?.low_price_bcx ? parseFloat(card.low_price_bcx) : null;
    if (marketPriceBcx && maxPriceBcx > marketPriceBcx) {
      const pricePercentage = ((maxPriceBcx / marketPriceBcx - 1) * 100).toFixed(1);
      const warningMessage = `⚠️ Warning: Your bid ($${maxPriceBcx.toFixed(3)}/BCX) is ${pricePercentage}% above the current market price ($${marketPriceBcx.toFixed(3)}/BCX). Are you sure you want to create this bid?`;
      
      if (!window.confirm(warningMessage)) {
        return; // User canceled, stay in add mode
      }
    }
    
    // Clear any validation errors since we passed all checks
    setError(null);
    
    // Create bid data in SplTrader format
    const bidData = {
      id: bidsService.formatCardId(card, details),
      max_price_bcx: maxPriceBcx,
      max_card_bcx: maxBcxCard,
      total_bcx: totalBcx,
      active: newBidValues.active !== false,
      use_pm_bid_price: false
    };

    // Show confirmation dialog instead of directly creating bid
    showBidCreationInfo(bidData);
  };
  

  
  // Start inline editing for existing bid
  const startInlineEdit = (bid) => {
    // Check for auto-level cards and provide info (not auto-correcting)
    const parsedCard = bidsService.parseCardId(bid.id);
    const foilType = parsedCard?.foil || 'regular';
    const requiredMaxBcx = getMaxLevelBcx(details, foilType);
    
    let correctedMaxBcxCard = bid.maxBcxCard.toString();
    
    if (requiredMaxBcx && isAutoMaxLevel(foilType) && bid.maxBcxCard !== requiredMaxBcx) {
      const rarityNames = { 1: 'Common', 2: 'Rare', 3: 'Epic', 4: 'Legendary' };
      const rarityName = rarityNames[details?.rarity] || 'Unknown';
      
      const maxCards = Math.floor(bid.totalBcx / requiredMaxBcx);
      const actualBcxToBuy = maxCards * requiredMaxBcx;
      const remainingBcx = bid.totalBcx - actualBcxToBuy;
      
      let infoMessage = `ℹ️ ${foilType} ${rarityName} cards require ${requiredMaxBcx} BCX per card.\n\n`;
      infoMessage += `Current bid: ${bid.totalBcx} total BCX, max ${bid.maxBcxCard} BCX per card\n`;
      infoMessage += `Result: ${maxCards} card${maxCards !== 1 ? 's' : ''} can be purchased (${actualBcxToBuy} BCX used)`;
      if (remainingBcx > 0) {
        infoMessage += `, ${remainingBcx} BCX unused`;
      }
      infoMessage += `\n\nYou can edit these values as needed.`;
      
      // Just show info, don't auto-correct
      alert(infoMessage);
    }
    
    setEditingBidId(bid.id);
    setEditValues({
      maxPriceBcx: bid.maxPriceBcx.toString(),
      totalBcx: bid.totalBcx.toString(),
      maxBcxCard: correctedMaxBcxCard,
      active: bid.active
    });
  };


  
  // Cancel inline editing
  const cancelInlineEdit = () => {
    setEditingBidId(null);
    setEditValues({});
  };
  
  // Save inline edits
  const saveInlineEdit = async (bidId) => {
    try {
      const bid = bids.find(b => b.id === bidId);
      if (!bid) {
        setError("Bid not found");
        return;
      }

      const maxPriceBcx = parseFloat(editValues.maxPriceBcx);
      const totalBcx = parseInt(editValues.totalBcx);
      const maxBcxCard = parseInt(editValues.maxBcxCard);

      // Validate inputs - stay in edit mode if validation fails
      if (!editValues.maxPriceBcx || isNaN(maxPriceBcx) || maxPriceBcx <= 0) {
        setError("Please enter a valid price per BCX (must be greater than 0)");
        return; // Stay in edit mode
      }
      
      if (!editValues.totalBcx || isNaN(totalBcx) || totalBcx <= 0) {
        setError("Please enter a valid total BCX (must be greater than 0)");
        return; // Stay in edit mode
      }
      
      if (!editValues.maxBcxCard || isNaN(maxBcxCard) || maxBcxCard <= 0) {
        setError("Please enter a valid max BCX per card (must be greater than 0)");
        return; // Stay in edit mode
      }
      
      if (maxBcxCard > totalBcx) {
        setError("Max BCX per card cannot be greater than total BCX. Please adjust your values.");
        return; // Stay in edit mode so user can fix the values
      }

      // Clear any validation errors since we passed all checks
      setError(null);

      // Check if bid price is above market price (warning but not blocking)
      const marketPriceBcx = card?.low_price_bcx ? parseFloat(card.low_price_bcx) : null;
      if (marketPriceBcx && maxPriceBcx > marketPriceBcx) {
        const pricePercentage = ((maxPriceBcx / marketPriceBcx - 1) * 100).toFixed(1);
        const warningMessage = `⚠️ Warning: Your updated bid ($${maxPriceBcx.toFixed(3)}/BCX) is ${pricePercentage}% above the current market price ($${marketPriceBcx.toFixed(3)}/BCX). Do you want to save this bid?`;
        
        if (!window.confirm(warningMessage)) {
          return; // User canceled, stay in edit mode
        }
      }
      
      // Clear any validation errors since we passed all checks
      setError(null);
      
      // Create updated bid data in SplTrader format
      const bidData = {
        id: bidId,
        max_price_bcx: maxPriceBcx,
        max_card_bcx: maxBcxCard,
        total_bcx: totalBcx,
        active: editValues.active,
        use_pm_bid_price: false
      };

      // Send to SplTrader API
      await bidsService.createOrUpdateBid(bidData);
      
      // Reload bids to show the updated data
      await loadBids();
      
      // Only exit edit mode after successful save
      setEditingBidId(null);
      setEditValues({});
    } catch (error) {
      setError("Error updating bid: " + error.message);
      // Stay in edit mode so user can retry or fix the issue
    }
  };
  
  // Handle input changes for editing
  const handleEditValueChange = (field, value) => {
    setEditValues(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Handle input changes for new bid
  const handleNewBidValueChange = (field, value) => {
    setNewBidValues(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      // Auto-correct for special cards when maxBcxCard is changed
      if (field === 'maxBcxCard') {
        const parsedCard = bidsService.parseCardId(bidsService.formatCardId(card, details));
        const foilType = parsedCard?.foil || 'regular';
        const requiredMaxBcx = getMaxLevelBcx(details, foilType);
        
        if (requiredMaxBcx && isAutoMaxLevel(foilType)) {
          const enteredValue = parseInt(value);
          if (!isNaN(enteredValue) && enteredValue !== requiredMaxBcx) {
            // Auto-correct to required value with a small delay so user sees what happened
            setTimeout(() => {
              setNewBidValues(prevState => ({
                ...prevState,
                maxBcxCard: requiredMaxBcx.toString()
              }));
              
              const rarityNames = { 1: 'Common', 2: 'Rare', 3: 'Epic', 4: 'Legendary' };
              const rarityName = rarityNames[details?.rarity] || 'Unknown';
              setError(`ℹ️ Auto-corrected: ${foilType} ${rarityName} cards require ${requiredMaxBcx} BCX per card.`);
              
              // Clear the info message after 3 seconds
              setTimeout(() => setError(null), 3000);
            }, 500);
          }
        }
      }
      
      return updated;
    });
  };
  
  // Handle bid deletion
  const handleDeleteBid = async (bidId) => {
    try {
      // Send delete request to SplTrader API
      await bidsService.deleteBid(bidId);
      
      // Reload bids to reflect the deletion
      await loadBids();
      
      setError(null);
    } catch (error) {
      setError("Error deleting bid: " + error.message);
    }
  };

  // Helper function to check if bid price is above market price
  const isPriceAboveMarket = (bidPrice, marketPrice) => {
    if (!marketPrice || !bidPrice) return false;
    const price = parseFloat(bidPrice);
    return !isNaN(price) && price > marketPrice;
  };

  // Helper function to get max level BCX for Black Foil and Gold Arcana cards
  const getMaxLevelBcx = (cardDetails, foilType) => {
    if (!cardDetails || !foilType) return null;
    
    // Only for Black Foil and Gold Arcana cards (they are always max level)
    if (foilType !== 'black' && foilType !== 'gold_arcane' && foilType !== 'black_arcane') {
      return null;
    }
    
    const rarity = cardDetails.rarity;
    
    // BCX needed for max level based on rarity
    const maxLevelBcx = {
      1: 38,  // Common
      2: 22,  // Rare  
      3: 10,  // Epic
      4: 4    // Legendary
    };
    
    return maxLevelBcx[rarity] || null;
  };

  // Check if card is auto-max level (Black Foil or Gold Arcana)
  const isAutoMaxLevel = (foilType) => {
    return foilType === 'black' || foilType === 'gold_arcane' || foilType === 'black_arcane';
  };

  // Show bid confirmation dialog
  const showBidCreationInfo = (bidData) => {
    // Check if user has disabled the info dialog
    const skipInfo = localStorage.getItem('skipBidCreationInfo') === 'true';
    
    if (skipInfo) {
      // Proceed directly with bid creation
      return proceedWithBidCreation(bidData);
    }
    
    setPendingBidData(bidData);
    setShowBidConfirmDialog(true);
  };

  // Proceed with bid creation
  const proceedWithBidCreation = async (bidData) => {
    try {
      // Save the "don't show again" preference
      if (dontShowBidInfoAgain) {
        localStorage.setItem('skipBidCreationInfo', 'true');
      }
      
      // Close dialog first
      setShowBidConfirmDialog(false);
      setPendingBidData(null);
      setDontShowBidInfoAgain(false);
      
      // Create the bid
      await bidsService.createOrUpdateBid(bidData);
      
      // Reload bids and close adding form
      await loadBids();
      setIsAddingNew(false);
      setNewBidValues({
        maxPriceBcx: "",
        totalBcx: "10",
        maxBcxCard: "1",
        active: true
      });
      
      setError(null);
    } catch (error) {
      console.error("Error creating bid:", error);
      setError("Error creating bid: " + error.message);
    }
  };

  // Cancel bid creation
  const cancelBidCreation = () => {
    setShowBidConfirmDialog(false);
    setPendingBidData(null);
    setDontShowBidInfoAgain(false);
  };



  // Calculate market price for easy access
  const marketPriceBcx = card?.low_price_bcx ? parseFloat(card.low_price_bcx) : null;

  
  return (
    <Box>
      {/* Market Header - only show when explicitly requested */}
      {showHeader && (
        <MarketHeader 
          title="Card Bids System"
          onInfoClick={() => setShowInfo(true)}
        />
      )}
      
      {/* Authority Management */}
      <CompactAuthorityToggle onAuthorityChange={handleAuthorityChange} />
      
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h6" fontWeight="bold">
            My Bids
          </Typography>
          <Tooltip title="How bids work">
            <IconButton
              onClick={() => setShowInfo(true)}
              size="small"
              sx={{ 
                color: colors.blueAccent[400],
                '&:hover': {
                  bgcolor: `${colors.blueAccent[400]}20`
                }
              }}
            >
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<AddIcon />}
          onClick={handleAddBid}
          disabled={isAddingNew || editingBidId !== null || !hasAuthority}
          size="small"
        >
          Add Bid
        </Button>
      </Box>
      
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      ) : !hasAuthority ? (
        <Box p={3} textAlign="center" bgcolor={colors.primary[400]} borderRadius={1}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Authority is required to create and manage bids. Please enable authority above to continue.
          </Alert>
          <Typography variant="body1" color={colors.grey[300]}>
            Once you grant authority, you'll be able to place bids on this card.
          </Typography>
        </Box>
      ) : bids.length === 0 && !isAddingNew ? (
        <Box p={3} textAlign="center" bgcolor={colors.primary[400]} borderRadius={1}>
          <Typography variant="body1" color={colors.grey[300]}>
            You haven't placed any bids on this card yet.
          </Typography>
        </Box>
      ) : (
        <TableContainer 
          component={Paper} 
          sx={{ 
            bgcolor: colors.primary[400],
            maxHeight: { 
              xs: 400, 
              md: 500, 
              lg: 600, 
              xl: 700 
            },
            overflow: 'auto'
          }}
        >
          <Table size="medium" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", fontSize: '0.95rem' }}>Card</TableCell>
                <TableCell sx={{ fontWeight: "bold", fontSize: '0.95rem' }}>Price per BCX</TableCell>
                <TableCell sx={{ fontWeight: "bold", fontSize: '0.95rem' }}>Total BCX</TableCell>
                <TableCell sx={{ fontWeight: "bold", fontSize: '0.95rem' }}>Purchased</TableCell>
                <TableCell sx={{ fontWeight: "bold", fontSize: '0.95rem' }}>Max BCX per Card</TableCell>
                <TableCell sx={{ fontWeight: "bold", fontSize: '0.95rem' }}>Active</TableCell>
                <TableCell sx={{ fontWeight: "bold", fontSize: '0.95rem' }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* New bid row when adding */}
              {isAddingNew && (
                <TableRow sx={{ 
                  bgcolor: `${colors.blueAccent[700]}20`,
                  animation: 'fadeIn 0.3s ease-in-out',
                  '@keyframes fadeIn': {
                    from: { opacity: 0, transform: 'translateY(-10px)' },
                    to: { opacity: 1, transform: 'translateY(0)' }
                  }
                }}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: colors.blueAccent[400] }}>
                        {details?.name || "Card Name"} (New Bid)
                      </Typography>
                      <Typography variant="caption" color={colors.grey[400]}>
                        ID: {bidsService.formatCardId(card, details)} ({getFoilTypeName(card?.foil !== undefined ? card.foil : 0).toLowerCase()})
                      </Typography>
                      {(() => {
                        const parsedCard = bidsService.parseCardId(bidsService.formatCardId(card, details));
                        const foilType = parsedCard?.foil || 'regular';
                        const maxBcx = getMaxLevelBcx(details, foilType);
                        
                        if (maxBcx && isAutoMaxLevel(foilType)) {
                          const rarityNames = { 1: 'Common', 2: 'Rare', 3: 'Epic', 4: 'Legendary' };
                          const rarityName = rarityNames[details?.rarity] || 'Unknown';
                          
                          return (
                            <Typography variant="caption" sx={{ 
                              display: 'block',
                              color: colors.yellow[400],
                              fontSize: '0.65rem',
                              fontWeight: 'bold',
                              mt: 0.3
                            }}>
                              ⚡ Auto-filled: {rarityName} {foilType} cards need {maxBcx} BCX
                            </Typography>
                          );
                        }
                        return null;
                      })()}
                    </Box>
                  </TableCell>
                  
                  {/* Price per BCX - user input */}
                  <TableCell>
                    <Box>
                      <TextField
                        value={newBidValues.maxPriceBcx}
                        onChange={(e) => handleNewBidValueChange('maxPriceBcx', e.target.value)}
                        type="number"
                        variant="standard"
                        placeholder="Price per BCX"
                        autoFocus
                        InputProps={{
                          endAdornment: <InputAdornment position="end">USD</InputAdornment>,
                          inputProps: { min: 0.001, step: 0.001, style: { width: '80px' } }
                        }}
                        size="small"
                        sx={{
                          '& .MuiInput-underline:before': {
                            borderBottomColor: isPriceAboveMarket(newBidValues.maxPriceBcx, marketPriceBcx) 
                              ? colors.redAccent[400] : colors.blueAccent[400]
                          },
                          '& .MuiInput-underline:after': {
                            borderBottomColor: isPriceAboveMarket(newBidValues.maxPriceBcx, marketPriceBcx) 
                              ? colors.redAccent[200] : colors.blueAccent[200]
                          }
                        }}
                      />
                      {/* Market price indicator and warning */}
                      <Box sx={{ mt: 0.5 }}>
                        {marketPriceBcx && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              display: 'block',
                              color: colors.blueAccent[400],
                              fontSize: '0.7rem'
                            }}
                          >
                            Market: ${marketPriceBcx.toFixed(3)}/BCX
                          </Typography>
                        )}
                        {isPriceAboveMarket(newBidValues.maxPriceBcx, marketPriceBcx) && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              display: 'block',
                              color: colors.redAccent[400],
                              fontSize: '0.7rem',
                              fontWeight: 'bold'
                            }}
                          >
                            ⚠️ Above market price!
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  
                  {/* Total BCX */}
                  <TableCell>
                    <Box>
                      <TextField
                        value={newBidValues.totalBcx}
                        onChange={(e) => handleNewBidValueChange('totalBcx', e.target.value)}
                        type="number"
                        variant="standard"
                        placeholder="Total BCX"
                        InputProps={{ 
                          inputProps: { min: 1, style: { width: '70px' } }
                        }}
                        size="small"
                        sx={{
                          '& .MuiInput-underline:before': {
                            borderBottomColor: colors.blueAccent[400]
                          },
                          '& .MuiInput-underline:after': {
                            borderBottomColor: colors.blueAccent[200]
                          }
                        }}
                      />
                      {/* Total amount display */}
                      <Box sx={{ mt: 0.5 }}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            display: 'block',
                            color: colors.blueAccent[300],
                            fontSize: '0.7rem',
                            fontWeight: 'bold'
                          }}
                        >
                          ${(parseFloat(newBidValues.maxPriceBcx || 0) * parseFloat(newBidValues.totalBcx || 0)).toFixed(3)}
                          {/* Warning for uneven BCX amounts on special cards - inline */}
                          {(() => {
                            const parsedCard = bidsService.parseCardId(bidsService.formatCardId(card, details));
                            const foilType = parsedCard?.foil || 'regular';
                            const maxBcx = getMaxLevelBcx(details, foilType);
                            
                            if (maxBcx && isAutoMaxLevel(foilType)) {
                              const totalBcx = parseInt(newBidValues.totalBcx || 0);
                              const maxBcxCard = parseInt(newBidValues.maxBcxCard || 0);
                              
                              if (totalBcx > 0 && maxBcxCard > 0) {
                                const maxCards = Math.floor(totalBcx / maxBcxCard);
                                const actualBcxToBuy = maxCards * maxBcxCard;
                                const remainingBcx = totalBcx - actualBcxToBuy;
                                
                                if (remainingBcx > 0) {
                                  return ` (⚠️ ${maxCards} card${maxCards !== 1 ? 's' : ''}, ${remainingBcx} BCX unused)`;
                                } else if (maxCards > 0) {
                                  return ` (✓ ${maxCards} card${maxCards !== 1 ? 's' : ''})`;
                                }
                              }
                            }
                            return null;
                          })()}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  
                  {/* Purchased BCX - Empty for new bids */}
                  <TableCell>
                    <Typography variant="body2" sx={{ color: colors.grey[400] }}>
                      -
                    </Typography>
                  </TableCell>
                  

                  
                  {/* Max BCX per Card */}
                  <TableCell>
                    <Box>
                      <TextField
                        value={newBidValues.maxBcxCard}
                        onChange={(e) => handleNewBidValueChange('maxBcxCard', e.target.value)}
                        type="number"
                        variant="standard"
                        placeholder="Max per card"
                        InputProps={{ 
                          inputProps: { min: 1, style: { width: '70px' } }
                        }}
                        size="small"
                        sx={{
                          '& .MuiInput-underline:before': {
                            borderBottomColor: colors.blueAccent[400]
                          },
                          '& .MuiInput-underline:after': {
                            borderBottomColor: colors.blueAccent[200]
                          }
                        }}
                      />
                      {/* Auto-level warning for special cards */}
                      {(() => {
                        const parsedCard = bidsService.parseCardId(bidsService.formatCardId(card, details));
                        const foilType = parsedCard?.foil || 'regular';
                        const maxBcx = getMaxLevelBcx(details, foilType);
                        
                        if (maxBcx && isAutoMaxLevel(foilType)) {
                          const enteredBcx = parseInt(newBidValues.maxBcxCard);
                          const needsCorrection = !isNaN(enteredBcx) && enteredBcx !== maxBcx;
                          
                          return (
                            <Box sx={{ mt: 0.5 }}>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  display: 'block',
                                  color: needsCorrection ? colors.redAccent[400] : colors.grey[400],
                                  fontSize: '0.65rem',
                                  fontWeight: needsCorrection ? 'bold' : 'normal'
                                }}
                              >
                                {needsCorrection ? `⚠️ Auto: ${maxBcx} BCX` : `Auto-max: ${maxBcx} BCX`}
                              </Typography>
                            </Box>
                          );
                        }
                        return null;
                      })()}
                    </Box>
                  </TableCell>
                  
                  {/* Active Status */}
                  <TableCell>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => handleNewBidValueChange('active', !newBidValues.active)}
                      sx={{ 
                        color: newBidValues.active ? colors.greenAccent[500] : colors.redAccent[400],
                        fontWeight: "bold",
                        p: 0,
                        '&:hover': {
                          bgcolor: newBidValues.active ? `${colors.greenAccent[500]}20` : `${colors.redAccent[400]}20`
                        }
                      }}
                    >
                      {newBidValues.active ? "Yes" : "No"}
                    </Button>
                  </TableCell>
                  
                  {/* Actions */}
                  <TableCell align="right">
                    <Tooltip title="Confirm and create bid">
                      <span>
                        <IconButton 
                          onClick={confirmNewBid}
                          size="small"
                          disabled={!newBidValues.maxPriceBcx || !newBidValues.totalBcx || !newBidValues.maxBcxCard}
                          sx={{ 
                            color: (!newBidValues.maxPriceBcx || !newBidValues.totalBcx || !newBidValues.maxBcxCard) 
                              ? colors.grey[500] 
                              : colors.greenAccent[400],
                            '&:hover': {
                              bgcolor: `${colors.greenAccent[400]}20`
                            }
                          }}
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Cancel">
                      <IconButton 
                        onClick={cancelNewBid}
                        size="small"
                        sx={{ 
                          color: colors.redAccent[400],
                          '&:hover': {
                            bgcolor: `${colors.redAccent[400]}20`
                          }
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              )}
              
              {/* Existing bids */}
              {bids.map((bid) => (
                <TableRow key={bid.id} sx={{
                  opacity: bid.active ? 1 : 0.6,
                  bgcolor: bid.active ? 'inherit' : `${colors.grey[700]}40`
                }}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {bid.cardName}
                      </Typography>
                      <Typography variant="caption" color={colors.grey[400]}>
                        ID: {bid.id} ({bid.foilType || 'regular'})
                      </Typography>
                    </Box>
                  </TableCell>
                  
                  {/* Price per BCX */}
                  <TableCell>
                    {editingBidId === bid.id ? (
                      <Box>
                        <TextField
                          value={editValues.maxPriceBcx}
                          onChange={(e) => handleEditValueChange('maxPriceBcx', e.target.value)}
                          type="number"
                          variant="standard"
                          InputProps={{
                            endAdornment: <InputAdornment position="end">USD</InputAdornment>,
                            inputProps: { min: 0.001, step: 0.001, style: { width: '80px' } }
                          }}
                          size="small"
                          sx={{
                            '& .MuiInput-underline:before': {
                              borderBottomColor: isPriceAboveMarket(editValues.maxPriceBcx, marketPriceBcx) 
                                ? colors.redAccent[400] : colors.blueAccent[400]
                            },
                            '& .MuiInput-underline:after': {
                              borderBottomColor: isPriceAboveMarket(editValues.maxPriceBcx, marketPriceBcx) 
                                ? colors.redAccent[200] : colors.blueAccent[200]
                            }
                          }}
                        />
                        {/* Show warning if editing price is above market */}
                        {isPriceAboveMarket(editValues.maxPriceBcx, marketPriceBcx) && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              display: 'block',
                              color: colors.redAccent[400],
                              fontSize: '0.65rem',
                              fontWeight: 'bold',
                              mt: 0.3
                            }}
                          >
                            ⚠️ Above market!
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      `$${bid.maxPriceBcx.toFixed(3)}`
                    )}
                  </TableCell>
                  
                  {/* Total BCX */}
                  <TableCell>
                    {editingBidId === bid.id ? (
                      <Box>
                        <TextField
                          value={editValues.totalBcx}
                          onChange={(e) => handleEditValueChange('totalBcx', e.target.value)}
                          type="number"
                          variant="standard"
                          InputProps={{ 
                            inputProps: { min: 1, style: { width: '60px' } }
                          }}
                          size="small"
                        />
                        {/* Total amount display during editing */}
                        <Box sx={{ mt: 0.5 }}>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              display: 'block',
                              color: colors.blueAccent[300],
                              fontSize: '0.7rem',
                              fontWeight: 'bold'
                            }}
                          >
                            ${(parseFloat(editValues.maxPriceBcx || 0) * parseFloat(editValues.totalBcx || 0)).toFixed(3)}
                            {/* Warning for uneven BCX amounts on special cards during editing - inline */}
                            {(() => {
                              const parsedCard = bidsService.parseCardId(bid.id);
                              const foilType = parsedCard?.foil || 'regular';
                              const maxBcx = getMaxLevelBcx(details, foilType);
                              
                              if (maxBcx && isAutoMaxLevel(foilType)) {
                                const totalBcx = parseInt(editValues.totalBcx || 0);
                                const maxBcxCard = parseInt(editValues.maxBcxCard || 0);
                                
                                if (totalBcx > 0 && maxBcxCard > 0) {
                                  const maxCards = Math.floor(totalBcx / maxBcxCard);
                                  const actualBcxToBuy = maxCards * maxBcxCard;
                                  const remainingBcx = totalBcx - actualBcxToBuy;
                                  
                                  if (remainingBcx > 0) {
                                    return ` (⚠️ ${maxCards} card${maxCards !== 1 ? 's' : ''}, ${remainingBcx} BCX unused)`;
                                  } else if (maxCards > 0) {
                                    return ` (✓ ${maxCards} card${maxCards !== 1 ? 's' : ''})`;
                                  }
                                }
                              }
                              return null;
                            })()}
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {bid.totalBcx}
                        </Typography>
                        {/* Total amount display */}
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            display: 'block',
                            color: colors.blueAccent[300],
                            fontSize: '0.7rem',
                            fontWeight: 'bold'
                          }}
                        >
                          ${(bid.maxPriceBcx * bid.totalBcx).toFixed(3)}
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  
                  {/* Purchased BCX */}
                  <TableCell>
                    {(() => {
                      const originalBcx = bid.originalTotalBcx || bid.totalBcx;
                      const purchasedBcx = originalBcx - bid.totalBcx;
                      
                      return (
                        <Box>
                          <Typography variant="body2" sx={{ 
                            color: purchasedBcx > 0 ? colors.greenAccent[400] : colors.grey[300],
                            fontWeight: purchasedBcx > 0 ? 'bold' : 'normal'
                          }}>
                            {purchasedBcx}
                          </Typography>
                          {purchasedBcx > 0 && (
                            <Typography variant="caption" sx={{ 
                              color: colors.grey[400],
                              fontSize: '0.65rem'
                            }}>
                              of {originalBcx}
                            </Typography>
                          )}
                        </Box>
                      );
                    })()}
                  </TableCell>
                  

                  
                  {/* Max BCX per Card */}
                  <TableCell>
                    {editingBidId === bid.id ? (
                      <Box>
                        <TextField
                          value={editValues.maxBcxCard}
                          onChange={(e) => handleEditValueChange('maxBcxCard', e.target.value)}
                          type="number"
                          variant="standard"
                          InputProps={{ 
                            inputProps: { min: 1, style: { width: '60px' } }
                          }}
                          size="small"
                        />
                        {/* Auto-level warning for special cards during editing */}
                        {(() => {
                          const parsedCard = bidsService.parseCardId(bid.id);
                          const foilType = parsedCard?.foil || 'regular';
                          const maxBcx = getMaxLevelBcx(details, foilType);
                          
                          if (maxBcx && isAutoMaxLevel(foilType)) {
                            const enteredBcx = parseInt(editValues.maxBcxCard);
                            const needsCorrection = !isNaN(enteredBcx) && enteredBcx !== maxBcx;
                            
                            return (
                              <Box sx={{ mt: 0.5 }}>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    display: 'block',
                                    color: needsCorrection ? colors.redAccent[400] : colors.grey[400],
                                    fontSize: '0.65rem',
                                    fontWeight: needsCorrection ? 'bold' : 'normal'
                                  }}
                                >
                                  {needsCorrection ? `⚠️ Auto: ${maxBcx} BCX` : `Auto-max: ${maxBcx} BCX`}
                                </Typography>
                              </Box>
                            );
                          }
                          return null;
                        })()}
                      </Box>
                    ) : (
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {bid.maxBcxCard}
                        </Typography>
                        {/* Auto-level info for special cards */}
                        {(() => {
                          const parsedCard = bidsService.parseCardId(bid.id);
                          const foilType = parsedCard?.foil || 'regular';
                          const maxBcx = getMaxLevelBcx(details, foilType);
                          
                          if (maxBcx && isAutoMaxLevel(foilType)) {
                            const isCorrect = bid.maxBcxCard === maxBcx;
                            
                            return (
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  display: 'block',
                                  color: isCorrect ? colors.greenAccent[400] : colors.redAccent[400],
                                  fontSize: '0.65rem',
                                  fontWeight: 'bold'
                                }}
                              >
                                {isCorrect ? `✓ Auto-max` : `⚠️ Should be ${maxBcx}`}
                              </Typography>
                            );
                          }
                          return null;
                        })()}
                      </Box>
                    )}
                  </TableCell>
                  
                  {/* Active Status */}
                  <TableCell>
                    {editingBidId === bid.id ? (
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => handleEditValueChange('active', !editValues.active)}
                        sx={{ 
                          color: editValues.active ? colors.greenAccent[500] : colors.redAccent[400],
                          fontWeight: "bold",
                          p: 0
                        }}
                      >
                        {editValues.active ? "Yes" : "No"}
                      </Button>
                    ) : (
                      <span style={{ 
                        color: bid.active ? colors.greenAccent[500] : colors.redAccent[400], 
                        fontWeight: "bold" 
                      }}>
                        {bid.active ? "Yes" : "No"}
                      </span>
                    )}
                  </TableCell>
                  
                  {/* Actions */}
                  <TableCell align="right">
                    {editingBidId === bid.id ? (
                      <>
                        <Tooltip title="Save changes">
                          <IconButton 
                            onClick={() => saveInlineEdit(bid.id)}
                            size="small"
                            sx={{ color: colors.greenAccent[400] }}
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton 
                            onClick={cancelInlineEdit}
                            size="small"
                            sx={{ color: colors.redAccent[400] }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <Tooltip title="Edit bid">
                          <IconButton 
                            onClick={() => startInlineEdit(bid)}
                            size="small"
                            sx={{ color: colors.blueAccent[400] }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete bid">
                          <IconButton 
                            onClick={() => handleDeleteBid(bid.id)}
                            size="small"
                            sx={{ color: colors.redAccent[400] }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Bids Info Dialog */}
      <BidsInfoDialog open={showInfo} onClose={() => setShowInfo(false)} />
      
      {/* Bid Creation Confirmation Dialog */}
      <Dialog 
        open={showBidConfirmDialog} 
        onClose={cancelBidCreation}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: colors.primary[400], color: colors.grey[100] }}>
          <Typography variant="h5" fontWeight="bold">
            Confirm Bid Creation
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: colors.primary[400], color: colors.grey[100], pt: 3 }}>
          {pendingBidData && (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  How your bid works:
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • Your bid will automatically purchase cards from the market when they are listed at or below your specified price
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • You'll buy up to <strong>{pendingBidData.total_bcx} BCX</strong> total, with a maximum of <strong>{pendingBidData.max_card_bcx} BCX per card</strong>
                </Typography>
                {(() => {
                  const parsedCard = bidsService.parseCardId(pendingBidData.id);
                  const maxBcx = getMaxLevelBcx(details, parsedCard.foil);
                  
                  if (maxBcx && isAutoMaxLevel(parsedCard.foil)) {
                    const maxCards = Math.floor(pendingBidData.total_bcx / pendingBidData.max_card_bcx);
                    const actualBcxToBuy = maxCards * pendingBidData.max_card_bcx;
                    const remainingBcx = pendingBidData.total_bcx - actualBcxToBuy;
                    
                    if (remainingBcx > 0) {
                      return (
                        <>
                          <Typography variant="body2" sx={{ mb: 1, color: colors.redAccent[300] }}>
                            • <strong>Important Note:</strong> You can purchase a maximum of <strong>{maxCards} card{maxCards !== 1 ? 's' : ''}</strong> ({pendingBidData.max_card_bcx} BCX each)
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1, color: colors.redAccent[300] }}>
                            • <strong>{remainingBcx} BCX will remain unused</strong> and cannot be used for a purchase
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1, color: colors.redAccent[300] }}>
                            • Actual purchase: <strong>{actualBcxToBuy} BCX</strong> for <strong>${(pendingBidData.max_price_bcx * actualBcxToBuy).toFixed(3)} USD</strong>
                          </Typography>
                        </>
                      );
                    }
                  }
                  return null;
                })()}
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • Your maximum total spend will be <strong>${(pendingBidData.max_price_bcx * pendingBidData.total_bcx).toFixed(3)} USD</strong>
                </Typography>
                <Typography variant="body2">
                  • The bid will remain active until all BCX are purchased or you deactivate it
                </Typography>
              </Alert>
              
              <Divider sx={{ my: 2, borderColor: colors.grey[600] }} />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ mb: 1, color: colors.blueAccent[300] }}>
                  Bid Details:
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Card:</strong> {details?.name || "Unknown Card"}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Foil Type:</strong> {(() => {
                    const parsedCard = bidsService.parseCardId(pendingBidData.id);
                    return parsedCard.foil || 'regular';
                  })()}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Price per BCX:</strong> ${pendingBidData.max_price_bcx} USD
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Total BCX to buy:</strong> {pendingBidData.total_bcx}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Max BCX per card:</strong> {pendingBidData.max_card_bcx}
                </Typography>
                
                {(() => {
                  const parsedCard = bidsService.parseCardId(pendingBidData.id);
                  const maxBcx = getMaxLevelBcx(details, parsedCard.foil);
                  
                  if (maxBcx && isAutoMaxLevel(parsedCard.foil)) {
                    const rarityNames = { 1: 'Common', 2: 'Rare', 3: 'Epic', 4: 'Legendary' };
                    const rarityName = rarityNames[details?.rarity] || 'Unknown';
                    
                    return (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          Special Card Type Detected!
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          This is a <strong>{parsedCard.foil}</strong> {rarityName} card. These cards are always max level and require <strong>{maxBcx} BCX</strong> to trigger a purchase.
                        </Typography>
                        {(() => {
                          const maxCards = Math.floor(pendingBidData.total_bcx / pendingBidData.max_card_bcx);
                          const actualBcxToBuy = maxCards * pendingBidData.max_card_bcx;
                          const remainingBcx = pendingBidData.total_bcx - actualBcxToBuy;
                          
                          if (remainingBcx > 0) {
                            return (
                              <Typography variant="body2" sx={{ color: colors.redAccent[300], fontWeight: 'bold' }}>
                                ⚠️ With {pendingBidData.total_bcx} BCX total, you can only purchase {maxCards} card{maxCards !== 1 ? 's' : ''}. {remainingBcx} BCX will remain unused.
                              </Typography>
                            );
                          }
                          return (
                            <Typography variant="body2" sx={{ color: colors.greenAccent[300], fontWeight: 'bold' }}>
                              ✓ Perfect division: {maxCards} card{maxCards !== 1 ? 's' : ''} at {maxBcx} BCX each
                            </Typography>
                          );
                        })()}
                      </Alert>
                    );
                  }
                  return null;
                })()}
              </Box>
            </>
          )}
          
          <FormControlLabel
            control={
              <Checkbox
                checked={dontShowBidInfoAgain}
                onChange={(e) => setDontShowBidInfoAgain(e.target.checked)}
                sx={{ color: colors.blueAccent[400] }}
              />
            }
            label="Don't show this dialog again"
            sx={{ color: colors.grey[300], mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ bgcolor: colors.primary[400], p: 3 }}>
          <Button 
            onClick={cancelBidCreation}
            variant="outlined"
            sx={{ 
              color: colors.grey[300],
              borderColor: colors.grey[500],
              '&:hover': {
                borderColor: colors.grey[400],
                bgcolor: `${colors.grey[500]}20`
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => proceedWithBidCreation(pendingBidData)}
            variant="contained"
            sx={{ 
              bgcolor: colors.blueAccent[500],
              color: colors.grey[100],
              '&:hover': {
                bgcolor: colors.blueAccent[400]
              }
            }}
          >
            Create Bid
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BidsTab;
